// Lesson videos: one film per lesson and language, made later by the video pipeline (tools/video) with the ids
// `crm-lNN-<topic>` (crm-l01-first-login … crm-l11-backup-security; see lessons.ts). Two ways to plug a film in, no code change needed for the second:
//   1. LESSON_VIDEOS below (bundled with the app), or
//   2. a manifest next to the app, `lessons/videos.json` (copied from apps/desktop/public/lessons/ by the build):
//        { "items": [ { "id": "crm-l01-first-login", "lang": "el", "src": "https://…/crm-l01-first-login.el.mp4",
//                       "poster": "https://….jpg", "captions": "https://….vtt" } ] }
// A lesson shows the film in the reader's language; when there is none yet it shows «video coming soon» and, if a
// film exists in another language, offers that one.
import { LANGS3, type Lang3 } from './l3.ts';

export interface LessonVideoSource { src: string; poster?: string | null; captions?: string | null }
export type LessonVideoMap = Record<string, Partial<Record<Lang3, LessonVideoSource>>>;

/** films bundled with the app (filled in when the video pipeline delivers them) */
export const LESSON_VIDEOS: LessonVideoMap = {};

/** https URLs, or a relative path inside the app (no «..», no other scheme); video/image/caption files only */
export function safeMediaUrl(u: unknown, kind: 'video' | 'image' | 'captions'): string | null {
  if (typeof u !== 'string') return null;
  const s = u.trim();
  if (!s || s.length > 1000) return null;
  const ext = kind === 'video' ? /\.(mp4|webm|m4v)(\?.*)?$/i : kind === 'image' ? /\.(jpe?g|png|webp)(\?.*)?$/i : /\.vtt(\?.*)?$/i;
  if (!ext.test(s)) return null;
  if (/^https:\/\//i.test(s)) { try { new URL(s); return s; } catch { return null; } }
  if (/^[a-z][a-z0-9+.-]*:/i.test(s) || s.startsWith('//') || s.split(/[\\/]/).includes('..')) return null;
  return s.replace(/^\.?\//, '');
}

/** read a manifest ({ items: [...] } or { "<id>": { "<lang>": { src } } }); anything unsafe or unknown is dropped */
export function parseVideoManifest(json: unknown): LessonVideoMap {
  const out: LessonVideoMap = {};
  const add = (id: unknown, lang: unknown, x: Record<string, unknown>) => {
    if (typeof id !== 'string' || !/^crm-l\d{2}-[a-z0-9-]{2,60}$/.test(id) || !LANGS3.includes(lang as Lang3)) return;
    const src = safeMediaUrl(x.src, 'video');
    if (!src) return;
    (out[id] ??= {})[lang as Lang3] = { src, poster: safeMediaUrl(x.poster, 'image'), captions: safeMediaUrl(x.captions ?? x.vtt, 'captions') };
  };
  if (!json || typeof json !== 'object') return out;
  const items = (json as { items?: unknown }).items;
  if (Array.isArray(items)) { for (const it of items) if (it && typeof it === 'object') add((it as Record<string, unknown>).id, (it as Record<string, unknown>).lang, it as Record<string, unknown>); return out; }
  for (const [id, langs] of Object.entries(json as Record<string, unknown>)) if (langs && typeof langs === 'object') for (const [lang, x] of Object.entries(langs as Record<string, unknown>)) if (x && typeof x === 'object') add(id, lang, x as Record<string, unknown>);
  return out;
}

export interface LessonVideoPick { source: LessonVideoSource | null; lang: Lang3; /** languages that do have a film */ available: Lang3[] }
/** the film for a lesson in a language (manifest entries override bundled ones) */
export function lessonVideo(videoId: string, lang: Lang3, manifest: LessonVideoMap = {}): LessonVideoPick {
  const merged = { ...(LESSON_VIDEOS[videoId] ?? {}), ...(manifest[videoId] ?? {}) };
  const available = LANGS3.filter((l) => !!merged[l]);
  return { source: merged[lang] ?? null, lang, available };
}
